import React, { useState, useEffect, useMemo } from 'react';
import apiService from '../services/ApiService';
import LoadingSkeleton from '../components/LoadingSkeleton';

const AdminManagement = () => {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isProcessing, setIsProcessing] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');

    const [formData, setFormData] = useState({
        phone_number: '',
        user_type: 'admin',
        name: '',
        email: ''
    });

    useEffect(() => {
        fetchUsers();
    }, []);

    const fetchUsers = async () => {
        setLoading(true);
        try {
            const result = await apiService.get('/users');
            // Filter for admins and agents only for this view
            const adminUsers = (result.users || []).filter(u =>
                u.user_type === 'admin' || u.user_type === 'agent' || u.user_type === 'superadmin'
            );
            setUsers(adminUsers);
        } catch (err) {
            console.error('Failed to fetch users:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleCreateUser = async (e) => {
        e.preventDefault();
        if (!formData.phone_number || formData.phone_number.length !== 10) {
            alert('Please enter a valid 10-digit phone number');
            return;
        }

        setIsProcessing(true);
        try {
            await apiService.post('/users/create-by-admin', formData);
            alert('User created successfully');
            setFormData({
                phone_number: '',
                user_type: 'admin',
                name: '',
                email: ''
            });
            setIsModalOpen(false);
            fetchUsers();
        } catch (err) {
            alert(err.message || 'Failed to create user');
        } finally {
            setIsProcessing(false);
        }
    };

    const filteredUsers = useMemo(() => {
        return users.filter(u =>
            u.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
            u.phone_number.includes(searchQuery) ||
            u.user_type.includes(searchQuery.toLowerCase())
        );
    }, [users, searchQuery]);

    if (loading) {
        return (
            <div className="admin-management-page">
                <div className="page-header" style={{ marginBottom: '24px' }}>
                    <h2>Admin Management</h2>
                </div>
                <div className="admin-card">
                    <LoadingSkeleton type="table" rows={6} />
                </div>
            </div>
        );
    }

    return (
        <div className="admin-management-page">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: '32px', gap: '20px', flexWrap: 'wrap' }}>
                <div>
                    <h2 style={{ fontFamily: 'Outfit', fontSize: '24px', fontWeight: '800', margin: '0', color: 'var(--text-main)' }}>Admin Management</h2>
                    <p style={{ color: 'var(--text-muted)', fontSize: '14px', marginTop: '4px' }}>Manage system administrators and agents.</p>
                </div>
                <button className="btn btn-primary" onClick={() => setIsModalOpen(true)}>
                    <i className="fas fa-plus"></i>
                    <span>Create Administrator</span>
                </button>
            </div>

            <div className="admin-card" style={{ padding: '0', overflow: 'hidden' }}>
                <div style={{ padding: '24px', borderBottom: '1px solid var(--border-light)', background: 'var(--bg-main)', display: 'flex', gap: '16px' }}>
                    <div style={{ position: 'relative', flex: 1 }}>
                        <i className="fas fa-search" style={{ position: 'absolute', left: '16px', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-dim)' }}></i>
                        <input
                            type="text"
                            placeholder="Search by name, phone or role..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            style={{
                                width: '100%',
                                padding: '12px 12px 12px 48px',
                                borderRadius: 'var(--radius-md)',
                                border: '1px solid var(--border-light)',
                                fontSize: '14px',
                                background: 'white',
                                outline: 'none',
                                boxSizing: 'border-box'
                            }}
                        />
                    </div>
                </div>

                <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead>
                            <tr style={{ background: 'var(--bg-main)', textAlign: 'left' }}>
                                <th style={tableHeaderStyle}>NAME & EMAIL</th>
                                <th style={tableHeaderStyle}>PHONE NUMBER</th>
                                <th style={tableHeaderStyle}>ROLE</th>
                                <th style={tableHeaderStyle}>JOINED</th>
                                <th style={tableHeaderStyle}>STATUS</th>
                                <th style={tableHeaderStyle}>ACTIONS</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredUsers.length > 0 ? filteredUsers.map((user, idx) => (
                                <tr key={user.user_id} style={{
                                    borderBottom: '1px solid var(--border-light)',
                                    transition: 'background 0.2s'
                                }} onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--primary-light)'}
                                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}>
                                    <td style={tableCellStyle}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                            <div style={{
                                                width: '36px', height: '36px', borderRadius: '50%',
                                                background: user.user_type === 'superadmin' ? 'var(--grad-primary)' : 'var(--border-light)',
                                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                color: user.user_type === 'superadmin' ? 'white' : 'var(--text-muted)',
                                                fontWeight: '700', fontSize: '14px'
                                            }}>
                                                {user.name ? user.name.charAt(0).toUpperCase() : '?'}
                                            </div>
                                            <div>
                                                <div style={{ fontWeight: '700', color: 'var(--text-main)' }}>{user.name || 'Anonymous'}</div>
                                                <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>{user.email || 'no-email@vaahanbuddy.com'}</div>
                                            </div>
                                        </div>
                                    </td>
                                    <td style={tableCellStyle}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                            <i className="fas fa-phone-alt" style={{ fontSize: '12px', color: 'var(--text-dim)' }}></i>
                                            <span style={{ fontWeight: '500' }}>{user.phone_number}</span>
                                        </div>
                                    </td>
                                    <td style={tableCellStyle}>
                                        <span className={`status-badge ${user.user_type === 'superadmin' ? 'status-completed' : 'status-in_progress'}`}>
                                            {user.user_type}
                                        </span>
                                    </td>
                                    <td style={tableCellStyle}>
                                        <div style={{ color: 'var(--text-muted)', fontSize: '13px' }}>
                                            {new Date(user.createdAt).toLocaleDateString('en-GB', { month: 'short', day: 'numeric', year: 'numeric' })}
                                        </div>
                                    </td>
                                    <td style={tableCellStyle}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                            <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: 'var(--success)' }}></div>
                                            <span style={{ fontSize: '13px', fontWeight: '600', color: 'var(--success)' }}>Active</span>
                                        </div>
                                    </td>
                                    <td style={tableCellStyle}>
                                        <div style={{ display: 'flex', gap: '8px' }}>
                                            <button className="btn btn-outline" style={{ padding: '6px', minWidth: '32px' }} title="Edit">
                                                <i className="fas fa-edit" style={{ fontSize: '12px' }}></i>
                                            </button>
                                            <button className="btn btn-outline" style={{ padding: '6px', minWidth: '32px', color: 'var(--error)' }} title="Delete">
                                                <i className="fas fa-trash-alt" style={{ fontSize: '12px' }}></i>
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            )) : (
                                <tr>
                                    <td colSpan="6" style={{ padding: '48px', textAlign: 'center', color: 'var(--text-muted)' }}>
                                        <i className="fas fa-users-slash" style={{ fontSize: '32px', display: 'block', marginBottom: '16px', opacity: 0.3 }}></i>
                                        No administrators found matching "{searchQuery}"
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {isModalOpen && (
                <div className="modal-overlay">
                    <div className="admin-card" style={{ maxWidth: '500px', width: '90%', padding: '0', overflow: 'hidden', border: 'none' }}>
                        <div style={{ padding: '24px', background: 'var(--grad-primary)', color: 'white', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <div>
                                <h3 style={{ margin: 0, fontSize: '20px', fontWeight: '800' }}>Create New User</h3>
                                <p style={{ margin: '4px 0 0', opacity: 0.8, fontSize: '12px' }}>Assign roles and grant access credentials.</p>
                            </div>
                            <button style={{ background: 'rgba(255,255,255,0.2)', border: 'none', color: 'white', fontSize: '20px', cursor: 'pointer', width: '32px', height: '32px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifySelf: 'center' }} onClick={() => setIsModalOpen(false)}>×</button>
                        </div>

                        <form onSubmit={handleCreateUser} style={{ padding: '24px' }}>
                            <div className="grid-responsive">
                                <div className="form-group col-12">
                                    <label style={labelStyle}>Full Name</label>
                                    <input
                                        type="text"
                                        name="name"
                                        style={inputStyle}
                                        value={formData.name}
                                        onChange={handleInputChange}
                                        placeholder="John Doe"
                                        required
                                    />
                                </div>

                                <div className="form-group col-12">
                                    <label style={labelStyle}>Email Address</label>
                                    <input
                                        type="email"
                                        name="email"
                                        style={inputStyle}
                                        value={formData.email}
                                        onChange={handleInputChange}
                                        placeholder="john@example.com"
                                        required
                                    />
                                </div>

                                <div className="form-group col-6 col-md-12">
                                    <label style={labelStyle}>Phone Number</label>
                                    <input
                                        type="tel"
                                        name="phone_number"
                                        style={inputStyle}
                                        value={formData.phone_number}
                                        onChange={(e) => setFormData(prev => ({ ...prev, phone_number: e.target.value.replace(/\D/g, '').slice(0, 10) }))}
                                        placeholder="10-digit number"
                                        required
                                    />
                                </div>

                                <div className="form-group col-6 col-md-12">
                                    <label style={labelStyle}>System Role</label>
                                    <select name="user_type" value={formData.user_type} onChange={handleInputChange} style={inputStyle}>
                                        <option value="admin">Administrator</option>
                                        <option value="agent">Support Agent</option>
                                        <option value="vehicle_owner">Vehicle Owner</option>
                                    </select>
                                </div>
                            </div>

                            <div style={{ marginTop: '32px', display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
                                <button type="button" className="btn btn-outline" onClick={() => setIsModalOpen(false)}>Cancel</button>
                                <button type="submit" className="btn btn-primary" disabled={isProcessing}>
                                    {isProcessing ? <><i className="fas fa-spinner fa-spin"></i> Processing...</> : 'Create User Account'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            <style>{`
                .modal-overlay { position: fixed; inset: 0; background: rgba(15, 23, 42, 0.7); backdrop-filter: blur(8px); display: flex; align-items: center; justify-content: center; z-index: 2000; padding: 20px; }
            `}</style>
        </div>
    );
};

const tableHeaderStyle = {
    padding: '16px 24px',
    color: 'var(--text-muted)',
    fontSize: '11px',
    fontWeight: '800',
    letterSpacing: '1px',
    borderBottom: '1px solid var(--border-light)'
};

const tableCellStyle = {
    padding: '20px 24px',
    fontSize: '14px',
    color: 'var(--text-main)'
};

const labelStyle = {
    display: 'block',
    marginBottom: '8px',
    fontWeight: '600',
    fontSize: '13px',
    color: 'var(--text-main)'
};

const inputStyle = {
    width: '100%',
    padding: '12px',
    borderRadius: 'var(--radius-md)',
    border: '1px solid var(--border-light)',
    fontSize: '14px',
    outline: 'none',
    boxSizing: 'border-box',
    background: '#fcfcfc',
    transition: 'all 0.2s'
};

export default AdminManagement;
